/**
 * Pure helpers for the dashboard SQL runner surface.
 * Kept free of React so keyboard binding and history can be unit-tested.
 */

export const SQL_HISTORY_MAX = 30;
export const SQL_HISTORY_KEY_PREFIX = "tb_sql_history";

export type SqlHistoryItem = {
  at: string;
  mode: string;
  sql: string;
  ok: boolean;
  ms?: number;
  message?: string;
};

/** Keyboard event shape used by isRunKeyboardShortcut (DOM or synthetic). */
export type RunShortcutKeyEvent = {
  key: string;
  metaKey: boolean;
  ctrlKey: boolean;
  shiftKey?: boolean;
  altKey?: boolean;
};

/**
 * True when the event should trigger the same Run path as the Run button.
 * Ctrl+Enter (Windows/Linux) or Cmd+Enter (macOS); Shift/Alt variants are ignored.
 */
export function isRunKeyboardShortcut(e: RunShortcutKeyEvent): boolean {
  if (e.key !== "Enter") return false;
  if (e.shiftKey || e.altKey) return false;
  return e.metaKey || e.ctrlKey;
}

/** Prepend a history item and cap length (newest first). */
export function appendSqlHistory<T>(
  items: readonly T[],
  item: T,
  max: number = SQL_HISTORY_MAX,
): T[] {
  return [item, ...items].slice(0, Math.max(0, max));
}

/** localStorage key for per-project SQL history. */
export function sqlHistoryStorageKey(projectId: string): string {
  return `${SQL_HISTORY_KEY_PREFIX}_${projectId}`;
}

/** Format a result cell for the results table. */
export function formatSqlCell(v: unknown): string {
  if (v == null) return "null";
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
}

/** 1-based line labels for a source string (at least one line). */
export function lineNumberLabels(source: string): number[] {
  const count = Math.max(1, source.split("\n").length);
  return Array.from({ length: count }, (_, i) => i + 1);
}
